import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  AvatarGroup,
  Box,
  IconButton,
  Tooltip,
  Button,
  Snackbar,
  Alert,
  Typography,
} from "@mui/material";
import VideocamIcon from "@mui/icons-material/Videocam";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useTranslation } from "react-i18next";
import {
  ACTIVE_VIDEO_ICON_SX,
  activeVideoTooltipKey,
  openAgentVideoJoinUrl,
} from "../../utils/activeVideoUi";

function useVideoPopupBlockedSnack() {
  const { t } = useTranslation("team");
  const [open, setOpen] = useState(false);
  const onBlocked = useCallback(() => setOpen(true), []);
  const snack = (
    <Snackbar
      open={open}
      autoHideDuration={6000}
      onClose={() => setOpen(false)}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert severity="warning" onClose={() => setOpen(false)} variant="filled">
        {t(
          "activeVideo.popupBlocked",
          "O navegador bloqueou a abertura da chamada. Permita pop-ups para este site e tente novamente."
        )}
      </Alert>
    </Snackbar>
  );
  return { onBlocked, snack };
}

function initialsFromName(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

function parseStartedAtMs(startedAt) {
  if (!startedAt) return null;
  if (startedAt instanceof Date) {
    const ms = startedAt.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  const raw = String(startedAt).trim();
  if (!raw) return null;
  // MySQL datetime without TZ → treat as local-ish ISO
  const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(raw)
    ? raw.replace(" ", "T")
    : raw;
  const ms = new Date(normalized).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function formatElapsed(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  }
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

function useRoomElapsedLabel(startedAt) {
  const startMs = useMemo(() => parseStartedAtMs(startedAt), [startedAt]);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!startMs) return undefined;
    setNowMs(Date.now());
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [startMs]);

  if (!startMs) return null;
  return formatElapsed(Math.floor((nowMs - startMs) / 1000));
}

function ActiveVideoPresence({ room, compact = false }) {
  const { t } = useTranslation("team");
  const participants = Array.isArray(room?.participantsInCall) ? room.participantsInCall : [];
  const elapsed = useRoomElapsedLabel(room?.startedAt);
  const namesTooltip = useMemo(() => {
    if (!participants.length) return t("activeVideo.waitingParticipants");
    return participants
      .map((p) => p.displayName || p.identity)
      .filter(Boolean)
      .join(", ");
  }, [participants, t]);

  if (!participants.length && !elapsed) return null;

  return (
    <Tooltip title={namesTooltip} enterDelay={400}>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          minWidth: 0,
          maxWidth: compact ? 140 : 220,
        }}
      >
        {participants.length ? (
          <AvatarGroup
            max={compact ? 3 : 4}
            sx={{
              flexShrink: 0,
              "& .MuiAvatar-root": {
                width: compact ? 22 : 26,
                height: compact ? 22 : 26,
                fontSize: "0.65rem",
                fontWeight: 700,
                borderWidth: 1.5,
              },
            }}
          >
            {participants.map((p) => {
              const name = p.displayName || p.identity || "?";
              return (
                <Avatar
                  key={p.identity || p.sid || name}
                  alt={name}
                  src={p.avatarUrl || undefined}
                  sx={{ bgcolor: "error.light", color: "error.contrastText" }}
                >
                  {initialsFromName(name)}
                </Avatar>
              );
            })}
          </AvatarGroup>
        ) : null}
        {elapsed ? (
          <Typography
            component="span"
            variant="caption"
            sx={{
              color: "error.main",
              fontWeight: 700,
              fontVariantNumeric: "tabular-nums",
              letterSpacing: 0.2,
              lineHeight: 1,
              whiteSpace: "nowrap",
            }}
            aria-label={t("activeVideo.durationAria", { duration: elapsed })}
          >
            {elapsed}
          </Typography>
        ) : null}
      </Box>
    </Tooltip>
  );
}

/**
 * Micro-sinal de chamada ativa (lista / picker). Clique no ícone = join se canJoin.
 */
export function ActiveVideoIndicator({
  room,
  onJoin,
  size = "small",
  stopPropagation = true,
}) {
  const { t } = useTranslation("team");
  const { onBlocked, snack } = useVideoPopupBlockedSnack();
  const isPrivate = Boolean(room?.isPrivate);
  const canJoin = Boolean(room?.canJoin);
  const joinUrl = room?.agentJoinUrl || room?.joinUrl || null;
  const tooltip = t(activeVideoTooltipKey({ isPrivate, canJoin }));

  const handleClick = useCallback(
    (e) => {
      if (stopPropagation) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (!room || !canJoin || !joinUrl) return;
      if (typeof onJoin === "function") {
        onJoin(room);
        return;
      }
      openAgentVideoJoinUrl(joinUrl, {
        context: room.context === "agenda" ? "agenda" : "team",
        onBlocked,
      });
    },
    [canJoin, joinUrl, onBlocked, onJoin, room, stopPropagation]
  );

  if (!room) return null;

  return (
    <>
      <Tooltip title={tooltip} enterDelay={400}>
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            position: "relative",
            flexShrink: 0,
            lineHeight: 0,
          }}
        >
          <IconButton
            size={size}
            onClick={handleClick}
            disabled={!canJoin}
            aria-label={tooltip}
            sx={{
              p: 0.5,
              color: "error.main",
              "&.Mui-disabled": {
                color: "error.main",
                opacity: 0.85,
              },
              "&:hover": canJoin
                ? { bgcolor: (theme) => theme.palette.action.hover }
                : undefined,
            }}
          >
            <VideocamIcon sx={ACTIVE_VIDEO_ICON_SX} />
          </IconButton>
          {isPrivate ? (
            <LockOutlinedIcon
              sx={{
                position: "absolute",
                right: -1,
                bottom: -1,
                fontSize: 10,
                color: "text.secondary",
                bgcolor: "background.paper",
                borderRadius: "50%",
                pointerEvents: "none",
              }}
            />
          ) : null}
        </Box>
      </Tooltip>
      {snack}
    </>
  );
}

/**
 * CTA curto no header do thread / dialog — só renderiza se canJoin (ou cadeado se privada).
 * Com chamada ativa: avatares de quem está na sala + duração + Entrar.
 */
export function ActiveVideoJoinControl({ room, onJoin, variant = "button" }) {
  const { t } = useTranslation("team");
  const { onBlocked, snack } = useVideoPopupBlockedSnack();
  const isPrivate = Boolean(room?.isPrivate);
  const canJoin = Boolean(room?.canJoin);
  const joinUrl = room?.agentJoinUrl || room?.joinUrl || null;

  const handleJoin = useCallback(() => {
    if (!room || !canJoin || !joinUrl) return;
    if (typeof onJoin === "function") {
      onJoin(room);
      return;
    }
    openAgentVideoJoinUrl(joinUrl, {
      context: room.context === "agenda" ? "agenda" : "team",
      onBlocked,
    });
  }, [canJoin, joinUrl, onBlocked, onJoin, room]);

  if (!room) return null;

  if (!canJoin) {
    if (!isPrivate) return null;
    return (
      <Tooltip title={t("activeVideo.tooltipPrivate")} enterDelay={400}>
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            color: "text.secondary",
            px: 0.5,
          }}
          aria-label={t("activeVideo.tooltipPrivate")}
        >
          <LockOutlinedIcon sx={{ fontSize: 16 }} />
        </Box>
      </Tooltip>
    );
  }

  if (variant === "icon") {
    return <ActiveVideoIndicator room={room} onJoin={onJoin} />;
  }

  return (
    <>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          minWidth: 0,
          flexShrink: 1,
        }}
      >
        <ActiveVideoPresence room={room} />
        <Button
          size="small"
          variant="text"
          color="error"
          startIcon={<VideocamIcon sx={{ fontSize: 16 }} />}
          onClick={handleJoin}
          sx={{ textTransform: "none", fontWeight: 600, minWidth: 0, px: 1 }}
        >
          {t("activeVideo.join")}
        </Button>
      </Box>
      {snack}
    </>
  );
}

export default ActiveVideoIndicator;
